import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function Premium() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [polling, setPolling] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get("/subscription/plans"),
        api.get("/subscription/me"),
      ]);
      setPlans(p.data);
      setSub(s.data);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll Stripe status after redirect back from Checkout
  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      setPolling(false);
      toast.error("Still processing. Refresh in a moment to confirm.");
      return;
    }
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPolling(false);
        toast.success("Welcome to Premium. Thank you.", { duration: 5000 });
        await refresh();
        await loadAll();
        // Clean URL
        const next = new URLSearchParams(searchParams);
        next.delete("session_id");
        setSearchParams(next, { replace: true });
        return;
      }
      if (data.status === "expired" || data.status === "unknown") {
        setPolling(false);
        // Clear the dangling session_id so the page doesn't show banner forever
        const next = new URLSearchParams(searchParams);
        next.delete("session_id");
        setSearchParams(next, { replace: true });
        if (data.status === "expired") {
          toast.error("Checkout session expired. Please try again.");
        }
        return;
      }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch {
      setPolling(false);
      toast.error("Couldn't confirm payment status.");
    }
  }, [refresh, loadAll, searchParams, setSearchParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      setPolling(true);
      pollStatus(sessionId);
    }
  }, [searchParams, pollStatus]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/checkout/session", { origin_url: window.location.origin });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout. Please try again.");
        setUpgrading(false);
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't start checkout.");
      setUpgrading(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-gold-soft tracking-overline text-xs">LOADING</div>;

  const isPremium = sub?.tier === "premium";

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 md:py-16" data-testid="premium-page">
      <div className="text-center mb-14">
        <div className="inline-flex items-center gap-3 mb-6">
          <span className="h-px w-12 bg-gold" />
          <span className="text-gold-soft tracking-overline text-xs">WANTED MEMBERSHIP</span>
          <span className="h-px w-12 bg-gold" />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
          Choose how you'd like to be <em className="not-italic text-gold">here</em>.
        </h1>
        <p className="mt-6 text-white/55 text-[15px] leading-relaxed max-w-xl mx-auto font-light">
          Both experiences are real. Both are warm. Premium simply unlocks a little more space —
          to write more, to look further, to be a little more visible.
        </p>
      </div>

      {polling && (
        <div className="mb-8 glass-strong rounded-2xl p-5 text-center" data-testid="polling-banner">
          <div className="text-gold-soft tracking-overline text-xs">CONFIRMING YOUR PAYMENT</div>
          <p className="text-white/60 text-sm mt-1">A moment please — this usually takes a few seconds.</p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((plan) => {
          const isCurrent = sub?.tier === plan.tier;
          const planIsPremium = plan.tier === "premium";
          return (
            <motion.div
              key={plan.tier}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`relative rounded-3xl p-10 ${
                planIsPremium
                  ? "bg-gradient-to-br from-[#1a1305] to-[#0a0805] border border-[#D4AF37]/30"
                  : "glass-strong"
              }`}
              data-testid={`plan-${plan.tier}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <div className="font-display text-2xl">{plan.name}</div>
                {planIsPremium && <Sparkles className="text-gold" size={18} />}
              </div>
              <div className="flex items-baseline gap-2 mb-8">
                <span className="font-display text-4xl font-light">{plan.price_display}</span>
                {plan.price_cents > 0 && <span className="text-white/40 text-sm">/ {plan.period}</span>}
              </div>

              <ul className="space-y-3 mb-10">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[15px] font-light">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      planIsPremium ? "bg-[#D4AF37]/90 text-[#050505]" : "border border-white/20 text-white/60"
                    }`}>
                      <Check size={11} strokeWidth={3} />
                    </span>
                    <span className="text-white/85">{f}</span>
                  </li>
                ))}
              </ul>

              {isCurrent ? (
                <div className="w-full py-3 text-center rounded-full border border-white/15 text-white/60 text-sm tracking-wide" data-testid={`current-plan-${plan.tier}`}>
                  Your current membership
                </div>
              ) : planIsPremium ? (
                <button
                  onClick={handleUpgrade}
                  disabled={upgrading || polling}
                  className="btn-gold w-full"
                  data-testid="upgrade-btn"
                >
                  {upgrading ? "Opening checkout..." : "Continue with Premium"}
                </button>
              ) : (
                <div className="w-full py-3 text-center rounded-full border border-white/15 text-white/40 text-sm tracking-wide">
                  Free, always
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Soft Founding Member note — quiet, never pushy */}
      {!isPremium && (
        <div className="mt-12 text-center max-w-2xl mx-auto">
          <p className="text-white/40 text-sm italic font-light leading-relaxed">
            Early members who join Wanted before launch may receive Founding Member access
            and preferred pricing as the community grows.
          </p>
        </div>
      )}

      {/* Quiet usage status */}
      {sub && !polling && (
        <div className="mt-10 text-center">
          <p className="text-xs tracking-overline text-white/30">
            {sub.tier === "premium"
              ? "MEMBER · UNLIMITED MESSAGING"
              : `MEMBER · ${sub.messages_used_today} OF ${sub.messages_limit_per_day} MESSAGES TODAY`}
          </p>
        </div>
      )}

      <div className="mt-16 text-center">
        <button
          onClick={() => navigate("/discover")}
          className="text-sm text-white/40 hover:text-white/70 transition-colors"
          data-testid="back-to-discover"
        >
          ← Back to Discover
        </button>
      </div>
    </div>
  );
}
